import React, { useCallback } from 'react';

import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';
import { TableCellsWithPermissions } from '@vlabs/shared/components/table/cells/index';
import { TableBottomPanelWithPermissions } from '@vlabs/shared/components/table/TableBottomPanelWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { routes } from '@vlabs/shared/navigation/routes';
import { actionColumnProps } from '@vlabs/shared/utils';
import {
  Panel, Table, openConfirmPopup, useModal,
} from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { CreateDynamicHandlerFrom } from './forms/CreateDynamicHandlerFrom';
import { selectPageData } from './selectors';
import {
  submitHandlerData, deleteHandler, updatePageSize, updatePage,
} from './store';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';

function HandlerListPage({
  data,
  createHandler,
  deleteHandler,
  setPageSize,
  setPageIndex,
}) {
  const { t } = useTranslation();
  const modal = useModal({ size: 'small' });
  const history = useHistory();

  const onEditPolicy = ({ handlerId }) => {
    history.push(generatePath(routes.handlers.read, { handlerId }));
  };

  const onCreatePolicy = () => {
    history.push(generatePath(routes.handlers.create));
  };

  const onDeleteHandler = ({ description, handlerId }) => {
    const message = t('handlers:подтверждение.удаление.текст', {
      handler: description,
    });

    const onConfirm = async () => {
      await deleteHandler(handlerId);
    };

    openConfirmPopup({
      title: t('handlers:удаление сценария'),
      message,
      type: 'delete',
      onConfirm,
    });
  };

  const onCreateHandler = async (values) => {
    await createHandler(values);
    modal.close();
  };

  const extraButtons = useCallback(() => (
    <ButtonWithPermissions
      data-testid="table.createDynamicHandlerButton"
      onClick={modal.open}
      permissions={{ rules: [permissions.handler.creation] }}
    >
      {t('handlers:таблица.кнопки.добавить динамический')}
    </ButtonWithPermissions>
  ), [t, modal.open]);

  return (
    <Page>
      <PageContent>
      {modal.wrap(<CreateDynamicHandlerFrom
        onSubmit={onCreateHandler}
      />)}
      <Panel className="Global__Table_fullScreen PanelAutoHeight">
        <Table
          columns={[
            { Header: t('handlers:описание'), accessor: 'description' },
            { Header: t('handlers:id сценария'), accessor: 'handlerId' },
            {
              Header: t('handlers:таблица.колонки.тип сценария'),
              accessor: 'isDynamic',
              Cell: ({ value }) => t('handlers:таблица.колонки.тип сценария', { context: value ? 'динамический' : 'статический' }),
            },
            actionColumnProps({
              id: 'action2',
              Cell: TableCellsWithPermissions.EditCell,
            }),
            actionColumnProps({
              id: 'action3',
              Cell: TableCellsWithPermissions.DeleteCell,
            }),
          ]}
          CustomBottomPanel={TableBottomPanelWithPermissions}
          data={data.data}
          extraButtons={extraButtons}
          meta={data.meta}
          onActions={{
            onAddRow: {
              handler: onCreatePolicy,
              label: t('handlers:таблица.кнопки.добавить статический'),
              permissions: { rules: [permissions.handler.creation] },
            },
            onEditRow: { handler: onEditPolicy },
            onDeleteRow: { handler: onDeleteHandler, permissions: { rules: [permissions.handler.deletion] } },
          }}
          pageIndex={data.pageIndex}
          pageSize={data.pageSize}
          pageSizeOptions={data.pageSizeOptions}
          paginationType="offsetBased"
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </Panel>
    </PageContent>
    </Page>
  );
}

HandlerListPage.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  createHandler: PropTypes.func.isRequired,
  deleteHandler: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  setPageIndex: PropTypes.func.isRequired,
};

HandlerListPage.defaultProps = {
  data: {},
};

export default connect(
  (state) => ({
    data: selectPageData(state),
  }),
  (dispatch) => ({
    createHandler: (data) => dispatch(submitHandlerData(undefined, data)),
    deleteHandler: (id) => dispatch(deleteHandler(id)),
    setPageSize: (pageSize) => dispatch(updatePageSize({ pageSize })),
    setPageIndex: (pageIndex) => dispatch(updatePage({ pageIndex })),
  }),
)(HandlerListPage);
